// Shimmering placeholder blocks shown while data loads. Size and shape come
// from the className (e.g. "h-4 w-1/2 rounded"); the shimmer lives in App.css.
export function Skeleton({ className = '', style }) {
  return <div className={`skeleton ${className}`} style={style} aria-hidden="true" />;
}

// Product card placeholder – square image, category, title, two lines of
// description, price and a button.
export function ProductCardSkeleton({ imageClassName = 'aspect-square' }) {
  return (
    <div className="bg-surface-container-lowest rounded-3xl overflow-hidden shadow-sm p-4 flex flex-col h-full">
      <Skeleton className={`${imageClassName} w-full rounded-2xl mb-6`} />
      <div className="px-2 pb-2 flex-1 flex flex-col">
        <Skeleton className="h-3 w-1/4 rounded mb-3" />
        <Skeleton className="h-5 w-3/4 rounded mb-3" />
        <Skeleton className="h-3 w-full rounded mb-2" />
        <Skeleton className="h-3 w-5/6 rounded mb-4" />
        <Skeleton className="h-5 w-1/3 rounded mb-4" />
        <Skeleton className="h-11 w-full rounded-xl mt-auto" />
      </div>
    </div>
  );
}

// Category card placeholder – square image, name, description and arrow.
export function CategoryCardSkeleton({ className = '' }) {
  return (
    <div className={`bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-sm flex flex-col ${className}`}>
      <Skeleton className="aspect-square w-full" />
      <div className="p-6 flex flex-col flex-grow">
        <Skeleton className="h-7 w-2/3 rounded mb-4" />
        <Skeleton className="h-3 w-full rounded mb-2" />
        <Skeleton className="h-3 w-full rounded mb-2" />
        <Skeleton className="h-3 w-1/2 rounded mb-6" />
        <Skeleton className="w-12 h-12 rounded-full self-end" />
      </div>
    </div>
  );
}

// ---- Dashboard ----

// Eyebrow label, page title and a line of description.
export function PageHeaderSkeleton({ withAction = false }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
      <div className="flex-1">
        <Skeleton className="h-3 w-32 rounded mb-3" />
        <Skeleton className="h-9 w-72 max-w-full rounded-lg mb-3" />
        <Skeleton className="h-4 w-full max-w-md rounded" />
      </div>
      {withAction && <Skeleton className="h-12 w-48 rounded-xl" />}
    </div>
  );
}

// Grid of KPI cards – icon, label and figure.
export function StatCardsSkeleton({ count = 4, className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6' }) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="bg-surface-container-lowest p-6 rounded-xl shadow-sm">
          <Skeleton className="w-10 h-10 rounded-lg mb-4" />
          <Skeleton className="h-3 w-24 rounded mb-3" />
          <Skeleton className="h-7 w-20 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

// Placeholder <tr>s, for dropping into an existing table's <tbody>. The first
// column gets a thumbnail when `withImage` is set.
export function TableRowsSkeleton({ columns, rows = 6, withImage = false, cellClassName = 'px-6 py-5' }) {
  const widths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-1/3', 'w-5/6'];
  return Array.from({ length: rows }).map((_, row) => (
    <tr key={row} className="border-t border-outline-variant/10">
      {Array.from({ length: columns }).map((_, col) => (
        <td key={col} className={cellClassName}>
          {col === 0 && withImage ? (
            <div className="flex items-center gap-4">
              <Skeleton className="w-14 h-14 rounded-xl flex-shrink-0" />
              <Skeleton className="h-4 w-32 rounded" />
            </div>
          ) : (
            <Skeleton
              className={`h-4 rounded ${col === columns - 1 ? 'w-16 ml-auto' : widths[(row + col) % widths.length]}`}
            />
          )}
        </td>
      ))}
    </tr>
  ));
}

// A whole table card (header row + body rows) for pages that render nothing
// else until their data arrives.
export function TableSkeleton({ columns = 5, rows = 6, withImage = false }) {
  return (
    <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/15 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-surface-container-low">
            <tr>
              {Array.from({ length: columns }).map((_, col) => (
                <th key={col} className="px-6 py-4">
                  <Skeleton className={`h-3 rounded ${col === columns - 1 ? 'w-12 ml-auto' : 'w-20'}`} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableRowsSkeleton columns={columns} rows={rows} withImage={withImage} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Title + subtitle placeholder for the dark blue page hero banners.
export function HeroTextSkeleton() {
  return (
    <div className="flex flex-col items-center" aria-label="Loading">
      <div className="h-10 md:h-14 w-2/3 md:w-1/2 rounded-lg bg-white/20 animate-pulse" />
      <div className="mt-4 h-5 w-5/6 md:w-2/3 rounded bg-white/15 animate-pulse" />
      <div className="mt-2 h-5 w-2/3 md:w-1/2 rounded bg-white/15 animate-pulse" />
    </div>
  );
}
